import { useState, useRef } from "react";

const FORM_ACTION = "https://readdy.ai/api/form/d99aug24irb0s7p1ve3g";

export default function LeadCaptureSection() {
  const formRef = useRef<HTMLFormElement>(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string>("");

  const formatPhone = (value: string): string => {
    const digits = value.replace(/\D/g, "");
    const len = digits.length;
    if (len === 0) return "";
    if (len <= 2) return `(${digits}`;
    if (len <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const masked = formatPhone(e.target.value);
    setFormData({ ...formData, phone: masked });
    if (errors.phone) {
      setErrors({ ...errors, phone: "" });
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = "Nome é obrigatório";
    if (!formData.email.trim()) {
      newErrors.email = "E-mail é obrigatório";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "E-mail inválido";
    }
    if (!formData.phone.trim()) newErrors.phone = "Telefone é obrigatório";
    if (!formData.service) newErrors.service = "Selecione um serviço";
    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");

    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    setSubmitting(true);

    try {
      const form = formRef.current;
      if (!form) return;

      const payload = new FormData(form);

      const honeypotValue = (payload.get("phone_alt") as string || "").trim();
      if (honeypotValue) {
        setSubmitted(true);
        return;
      }

      payload.delete("phone_alt");

      const response = await fetch(FORM_ACTION, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams(payload as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: any = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // not JSON
      }

      if (response.ok && parsed?.code === "OK") {
        setSubmitted(true);
      } else {
        const serverMsg =
          parsed?.meta?.message ||
          parsed?.message ||
          parsed?.meta?.detail ||
          responseText ||
          "Erro ao enviar o formulário. Tente novamente.";
        if (
          typeof serverMsg === "string" &&
          (serverMsg.toLowerCase().includes("spam") || serverMsg.toLowerCase().includes("form data is spam"))
        ) {
          setFormError("Não foi possível enviar sua mensagem. Por favor, tente novamente mais tarde.");
        } else {
          setFormError(typeof serverMsg === "string" ? serverMsg : "Erro ao enviar. Tente novamente.");
        }
      }
    } catch {
      setFormError("Erro de conexão. Verifique sua internet e tente novamente.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: "" });
    }
  };

  return (
    <section className="relative py-16 md:py-24 overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Panoramic%20cityscape%20of%20S%C3%A3o%20Paulo%20at%20dusk%20with%20modern%20skyscrapers%20and%20residential%20buildings%2C%20warm%20golden%20and%20teal%20ambient%20lighting%2C%20urban%20horizon%20view%2C%20professional%20city%20photography%2C%20clear%20sky%20with%20soft%20clouds%2C%20elegant%20metropolitan%20atmosphere&width=1600&height=700&seq=cta-bg-2026-x1&orientation=landscape"
          alt="Horizonte urbano"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/70 to-foreground-950/50"></div>
      </div>

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
              Solicite uma
              <br />
              Simulação Gratuita
            </h2>
            <p className="text-foreground-200 text-lg leading-relaxed mb-8 max-w-md">
              Preencha o formulário e um de nossos consultores entrará em
              contato com as melhores condições para o seu perfil.
            </p>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/30 text-primary-300">
                  <i className="ri-check-line text-xl"></i>
                </div>
                <div>
                  <p className="text-white font-medium">Simulação sem compromisso</p>
                  <p className="text-foreground-300 text-sm">Análise gratuita do seu perfil</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/30 text-primary-300">
                  <i className="ri-check-line text-xl"></i>
                </div>
                <div>
                  <p className="text-white font-medium">Atendimento personalizado</p>
                  <p className="text-foreground-300 text-sm">Consultor dedicado ao seu caso</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/30 text-primary-300">
                  <i className="ri-check-line text-xl"></i>
                </div>
                <div>
                  <p className="text-white font-medium">Resposta em até 24h</p>
                  <p className="text-foreground-300 text-sm">Agilidade no retorno</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 md:p-8 shadow-xl">
            {submitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
                  <i className="ri-check-line text-3xl"></i>
                </div>
                <h3 className="font-heading text-xl font-bold text-foreground-950 mb-2">
                  Solicitação Enviada!
                </h3>
                <p className="text-foreground-600 text-sm">
                  Obrigado pelo interesse. Nossa equipe entrará em contato em
                  breve com as melhores condições para você.
                </p>
                <button
                  onClick={() => {
                    setSubmitted(false);
                    setFormError("");
                    setFormData({
                      name: "",
                      email: "",
                      phone: "",
                      service: "",
                      message: "",
                    });
                  }}
                  className="mt-6 text-primary-600 text-sm font-medium hover:text-primary-700 transition-colors"
                >
                  Enviar nova solicitação
                </button>
              </div>
            ) : (
              <form
                ref={formRef}
                data-readdy-form
                id="lead-form"
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <input type="hidden" name="form_id" value="lead-form" />

                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="form-honeypot"
                />

                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                    Nome completo
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Seu nome"
                    className="w-full px-4 py-3 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                  />
                  {errors.name && (
                    <p className="text-accent-500 text-xs mt-1">{errors.name}</p>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                      E-mail
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="seu@email.com"
                      className="w-full px-4 py-3 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                    />
                    {errors.email && (
                      <p className="text-accent-500 text-xs mt-1">{errors.email}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                      Telefone / WhatsApp
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      placeholder="(11) 99999-9999"
                      inputMode="numeric"
                      maxLength={15}
                      className="w-full px-4 py-3 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                    />
                    {errors.phone && (
                      <p className="text-accent-500 text-xs mt-1">{errors.phone}</p>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                    Serviço de interesse
                  </label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all appearance-none"
                    style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center", paddingRight: "36px" }}
                  >
                    <option value="">Selecione uma opção</option>
                    <option value="financiamento">Financiamento Imobiliário</option>
                    <option value="home_equity">Home Equity</option>
                    <option value="consorcio">Consórcio</option>
                    <option value="outro">Outro</option>
                  </select>
                  {errors.service && (
                    <p className="text-accent-500 text-xs mt-1">{errors.service}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                    Mensagem (opcional)
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={3}
                    maxLength={500}
                    placeholder="Conte-nos mais sobre o que você precisa..."
                    className="w-full px-4 py-3 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                  ></textarea>
                  <p className="text-foreground-400 text-xs mt-1 text-right">
                    {formData.message.length}/500
                  </p>
                </div>

                {formError && (
                  <div className="p-3 rounded-xl bg-accent-100 border border-accent-200">
                    <p className="text-accent-700 text-sm">{formError}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-primary-500 text-white font-medium rounded-xl hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Enviando...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line text-lg"></i>
                      Solicitar Simulação
                    </>
                  )}
                </button>
                <p className="text-foreground-400 text-xs text-center">
                  Ao enviar, você concorda com nossa Política de Privacidade.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}